<script lang="ts">
	import { onMount, onDestroy } from "svelte";
	import { fly, slide } from "svelte/transition";
	import { yyyy, goto } from "./store.svelte";
	import { addOnsData } from "./lib/addOnsData";
	import "bulma-switch/dist/css/bulma-switch.min.css";
	import Fa from "svelte-fa";
	import {
		faUser,
		faChevronDown,
		faChevronRight,
		faUserShield,
		faCircleNotch,
		faUserSlash,
		faCheck,
		faTimes,
		faPlus,
		faTrash,
		faEdit,
		faMagnifyingGlass
	} from "@fortawesome/free-solid-svg-icons";

	// Store for the user data
	let users = $state([]);
	let expandedUsers = $state({});
	let loading = $state(true);
	let error = $state(null);
	let ready = $state(false);
	let searchTerm = $state("");
	let showAdminWarning = $state(false);
	let pendingAdminChange = $state(null);

	// Filtered users based on search term
	const filteredUsers = $derived(users.filter(user => 
		user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
		user.email.toLowerCase().includes(searchTerm.toLowerCase())
	));

	// Function to toggle expansion for a specific user (only one at a time)
	const toggleUser = (userId) => {
		const isCurrentlyExpanded = expandedUsers[userId];
		
		// Collapse all users first
		expandedUsers = {};
		
		// If the clicked user wasn't expanded, expand it
		if (!isCurrentlyExpanded) {
			expandedUsers[userId] = true;
		}
		
		expandedUsers = { ...expandedUsers }; // Force reactivity
	};

	// Function to fetch users from API
	const fetchUsers = async () => {
		loading = true;
		error = null;
		
		try {
			// Check if user is logged in
			if (!yyyy.sesh.logtoken) {
				goto("/");
				return;
			}

			const url = yyyy.uri.api;
			const headers = {
				"Content-Type": "application/json",
				"Authorization": "Bearer " + yyyy.sesh.logtoken.token,
			};

			const response = await fetch(`${url}/rpc/getuserlist`, {
				method: 'GET',
				headers: headers
			});
			
			if (!response.ok) {
				throw new Error(`Error fetching users: ${response.statusText}`);
			}
			
			const data = await response.json();
			
			// Transform the API response to match our component structure
			users = data.userlist.map((user, index) => ({
				id: index, // Using index as ID since there's no unique ID in the API response
				email: user.email,
				name: user.email.split('@')[0], // Extract name from email
				active: user.userinfo !== null, // User is active if userinfo is not null
				admin: user.userinfo?.admin || false,
				allowedApps: user.userinfo?.allowedApps || []
			}));
			
			// Initialize expanded state for each user
			users.forEach(user => {
				if (expandedUsers[user.id] === undefined) {
					expandedUsers[user.id] = false;
				}
			});
		} catch (err) {
			console.error("Failed to fetch users:", err);
			error = err.message;
		} finally {
			loading = false;
		}
	};

	// Function to update user on server
	const updateUser = async (email, userinfo) => {
		try {
			const url = yyyy.uri.api;
			const headers = {
				"Content-Type": "application/json",
				"Authorization": "Bearer " + yyyy.sesh.logtoken.token,
			};
			
			const body = JSON.stringify({
				useremail: email,
				newuserdata: userinfo
			});

			const response = await fetch(`${url}/rpc/updateuser`, {
				method: 'POST',
				headers: headers,
				body: body
			});

			if (!response.ok) {
				throw new Error(`Error updating user: ${response.statusText}`);
			}
			
			console.log("User updated successfully");
		} catch (err) {
			console.error("Failed to update user:", err);
			// You might want to show an error toast here
		}
	};

	// Function to toggle admin status
	const toggleAdmin = (userId) => {
		const user = users.find(u => u.id === userId);
		if (!user) return;

		const newAdminStatus = !user.admin;
		
		// Check if current user is trying to remove their own admin status
		const currentUserEmail = yyyy.sesh.tokeninfo?.email;
		
		if (currentUserEmail && user.email === currentUserEmail && user.admin && !newAdminStatus) {
			// Show custom warning modal
			pendingAdminChange = { userId, user, newAdminStatus };
			showAdminWarning = true;
			return; // Don't proceed until user confirms
		}

		// Update local state
		users = users.map(u => {
			if (u.id === userId) {
				return { ...u, admin: newAdminStatus };
			}
			return u;
		});

		// Update on server
		updateUser(user.email, {
			admin: newAdminStatus,
			allowedApps: user.allowedApps
		});
	};

	// Function to toggle user active status
	const toggleActive = (userId) => {
		const user = users.find(u => u.id === userId);
		if (!user) return;

		const newActiveStatus = !user.active;

		// Update local state
		users = users.map(u => {
			if (u.id === userId) {
				return { ...u, active: newActiveStatus };
			}
			return u;
		});

		// Update on server
		if (newActiveStatus) {
			// Activating user - set default userinfo
			updateUser(user.email, {
				admin: false,
				allowedApps: []
			});
		} else {
			// Deactivating user - set userinfo to null
			updateUser(user.email, null);
		}
	};

	// Function to add an app to a user
	const addApp = (userId, app) => {
		const user = users.find(u => u.id === userId);
		if (!user || user.allowedApps.includes(app)) return;

		const newAllowedApps = [...user.allowedApps, app];

		// Update local state
		users = users.map(u => {
			if (u.id === userId) {
				return { 
					...u, 
					allowedApps: newAllowedApps
				};
			}
			return u;
		});

		// Update on server
		updateUser(user.email, {
			admin: user.admin,
			allowedApps: newAllowedApps
		});
	};

	// Function to remove an app from a user
	const removeApp = (userId, app) => {
		const user = users.find(u => u.id === userId);
		if (!user) return;

		const newAllowedApps = user.allowedApps.filter(a => a !== app);

		// Update local state
		users = users.map(u => {
			if (u.id === userId) {
				return { 
					...u, 
					allowedApps: newAllowedApps
				};
			}
			return u;
		});

		// Update on server
		updateUser(user.email, {
			admin: user.admin,
			allowedApps: newAllowedApps
		});
	};

	// Handle admin warning modal confirmation
	const confirmAdminChange = () => {
		if (!pendingAdminChange) return;
		
		const { userId, user, newAdminStatus } = pendingAdminChange;
		
		// Update local state
		users = users.map(u => {
			if (u.id === userId) {
				return { ...u, admin: newAdminStatus };
			}
			return u;
		});

		// Update on server
		updateUser(user.email, {
			admin: newAdminStatus,
			allowedApps: user.allowedApps
		});
		
		// Close modal and reset
		showAdminWarning = false;
		pendingAdminChange = null;
	};

	const cancelAdminChange = () => {
		showAdminWarning = false;
		pendingAdminChange = null;
	};

	// Get available apps from addOnsData, excluding the base "livewire" and any not ready for action
	const availableApps = addOnsData
		.filter(addon => addon.id !== "livewire" && addon.readyForAction !== false)
		.map(addon => ({ id: addon.id, name: addon.name }));


	onMount(() => {
		// Check if user is logged in
		if (!yyyy.sesh.logtoken) {
			goto("/");
			return;
		}
		
		setTimeout(() => {
			ready = true;
		}, 400);
		
		fetchUsers();
	});

	onDestroy(() => {
		// Cleanup if needed
	});
</script>

<div class="user-manager-container">
	<div class="user-manager-content">
			<h1 class="title thin lvwr-title" class:dim={!ready} class:bri={ready}>
				User Manager
			</h1>
			<h2 class="subtitle lvwr-subtitle" class:dim={!ready} class:bri={ready}>
				Manage user accounts and permissions
			</h2>
			
			<div class="content lvwr-content" class:dim={!ready} class:bri={ready}>
				<div class="search-container">
					<div class="search-box">
						<input
							type="search"
							bind:value={searchTerm}
							placeholder="Search by email..."
							class="search-input"
						/>
						<div class="search-icon">
							<Fa icon={faMagnifyingGlass} />
						</div>
					</div>
				</div>

				<div class="users-content">
		{#if loading}
			<div class="loading-state">
				<Fa icon={faCircleNotch} spin size="2x" />
				<p>Loading users...</p>
			</div>
		{:else if error}
			<div class="error-state">
				<p>Error: {error}</p>
				<button onclick={fetchUsers} class="retry-button">Retry</button>
			</div>
		{:else if users.length === 0}
			<div class="empty-state">No users available</div>
		{:else if filteredUsers.length === 0}
			<div class="empty-state">No users match your search</div>
		{:else}
			<div class="users-list">
				{#each filteredUsers as user (user.id)}
					<div class="user-item">
						<div class="user-header" onclick={() => toggleUser(user.id)}>
							<span class="expand-icon">
								<Fa icon={expandedUsers[user.id] ? faChevronDown : faChevronRight} />
							</span>
							<span class="user-icon">
							<Fa icon={!user.active ? faUserSlash : user.admin ? faUserShield : faUser} />
							</span>
							<span class="user-name">{user.name}</span>
							<span class="user-email">{user.email}</span>
							<span class="user-badge" class:admin-badge={user.admin} class:inactive-badge={!user.active}>
								{!user.active ? 'Inactive' : user.admin ? 'Admin' : 'User'}
							</span>
						</div>

						{#if expandedUsers[user.id]}
							<div class="user-details" transition:slide={{ duration: 200 }}>
								<div class="user-detail-section">
									<div class="user-detail-header">
										<h4>User Details</h4>
									</div>
									<div class="user-detail-content">
										<div class="detail-item">
											<span class="detail-label">Account Status:</span>
											<div class="toggle-field">
												<label class="toggle-container">
													<span class="toggle-label">{user.active ? 'Active' : 'Deactivated'}</span>
													<input
														id="active-switch-{user.id}"
														type="checkbox"
														checked={user.active}
														onchange={() => toggleActive(user.id)}
													/>
													<span class="toggle-switch"></span>
												</label>
											</div>
										</div>
										{#if user.active}
											<div class="detail-item">
												<span class="detail-label">Admin Status:</span>
												<div class="toggle-field">
													<label class="toggle-container">
														<span class="toggle-label">{user.admin ? 'Admin' : 'Regular User'}</span>
														<input
															id="admin-switch-{user.id}"
															type="checkbox"
															checked={user.admin}
															onchange={() => toggleAdmin(user.id)}
														/>
														<span class="toggle-switch"></span>
													</label>
												</div>
											</div>
										{/if}
									</div>
								</div>

								{#if user.active}
								<div class="user-detail-section">
									<div class="user-detail-header">
										<h4>Allowed Applications</h4>
									</div>
									<div class="apps-list">
										{#each user.allowedApps as app}
											{@const appData = availableApps.find(a => a.id === app)}
											<div class="app-item">
												<span class="app-name">{appData?.name || app}</span>
												<button class="app-remove" onclick={() => removeApp(user.id, app)}>
													<Fa icon={faTimes} />
												</button>
											</div>
										{/each}
										<div class="app-item add-app">
											<select class="app-select" onchange={(e) => {
												if (e.target.value) {
													addApp(user.id, e.target.value);
													e.target.value = '';
												}
											}}>
												<option value="">Add application...</option>
												{#each availableApps.filter(app => !user.allowedApps.includes(app.id)) as app}
													<option value={app.id}>{app.name}</option>
												{/each}
											</select>
											<button class="app-add">
												<Fa icon={faPlus} />
											</button>
										</div>
									</div>
								</div>
								{/if}
							</div>
						{/if}
					</div>
				{/each}
			</div>
		{/if}
				</div>
			</div>
			<footer class="card-footer" class:dim={!ready} class:bri={ready}>
				<div class="card-footer-item sign-in-up-footer">
					<button
						onclick={() => goto("/livewires")}
						class="button lvwr-toggle"
					>
						Back to Livewires
					</button>
				</div>
			</footer>
		</div>
</div>

{#if showAdminWarning}
	<div class="modal-backdrop" onclick={cancelAdminChange}>
		<div class="modal-content" onclick={(e) => e.stopPropagation()}>
			<h3>⚠️ Warning</h3>
			<p>Removing your own admin privileges will be effective on the next login.</p>
			<p>You will need another admin to restore your privileges.</p>
			<div class="modal-buttons">
				<button class="button lvwr-toggle" onclick={cancelAdminChange}>Cancel</button>
				<button class="button lvwr-toggle is-danger" onclick={confirmAdminChange}>Continue</button>
			</div>
		</div>
	</div>
{/if}

<style>
	.user-manager-container {
		position: fixed;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		margin: auto;
		width: 80%;
		max-width: 1200px;
		height: 80vh;
		padding: 0 1rem;
		z-index: 5;
	}

	.user-manager-content {
		background-color: rgba(255, 255, 255, 0.95);
		backdrop-filter: blur(2px);
		border: 1px solid #dbdbdb;
		border-radius: 8px;
		padding: 2rem;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		height: 100%;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		position: relative;
	}

	.dim {
		opacity: 0%;
		transition: opacity 2.5s ease;
	}

	.bri {
		opacity: 100%;
		transition: opacity 2.5s ease;
	}

	.back-button {
		background: none;
		border: none;
		color: #ff3e00;
		cursor: pointer;
		font-size: 0.9rem;
		margin-bottom: 1rem;
		padding: 0.5rem 0;
		transition: all 0.2s ease;
	}

	.back-button:hover {
		color: #e63600;
		text-decoration: underline;
	}

	.back-button-top {
		position: absolute;
		top: 1rem;
		left: 1rem;
		z-index: 10;
		margin: 0;
	}

	.content.lvwr-content {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 0;
	}

	.search-container {
		margin-bottom: 1.5rem;
		margin-top: 1rem;
	}

	.search-box {
		position: relative;
		width: 100%;
	}

	.search-input {
		width: 100%;
		padding: 0.75rem 2.5rem 0.75rem 1rem;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		font-size: 0.9rem;
		transition: all 0.2s;
		background-color: rgba(255, 255, 255, 0.95);
		caret-color: #ff3e00;
	}

	.search-input:focus {
		outline: none;
		border-color: #ff3e00;
		box-shadow: 0 0 0 2px rgba(255, 62, 0, 0.1);
	}

	.search-input::placeholder {
		color: #999;
	}

	.search-icon {
		position: absolute;
		right: 0.75rem;
		top: 50%;
		transform: translateY(-50%);
		color: #999;
		pointer-events: none;
	}

	.users-content {
		flex: 1;
		overflow-y: auto;
		min-height: 0;
	}

	.loading-state, .empty-state, .error-state {
		text-align: center;
		padding: 2rem 1rem;
		color: #666;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
	}

	.error-state {
		color: #ff3860;
	}

	.retry-button {
		padding: 0.5rem 1rem;
		border: 1px solid #3273dc;
		background-color: #3273dc;
		color: white;
		border-radius: 4px;
		cursor: pointer;
		transition: all 0.2s;
	}

	.retry-button:hover {
		background-color: #2366d1;
		border-color: #2366d1;
	}

	.users-list {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.user-item {
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		overflow: hidden;
		background-color: rgba(255, 255, 255, 0.9);
	}

	.user-header {
		padding: 8px 10px;
		background-color: rgba(255, 255, 255, 0.95);
		backdrop-filter: blur(1px);
		display: flex;
		align-items: center;
		cursor: pointer;
		transition: all 0.2s;
		font-size: 0.85rem;
	}

	.user-header:hover {
		background-color: rgba(255, 62, 0, 0.05);
	}

	.expand-icon {
		margin-right: 6px;
		width: 12px;
		color: #ff3e00;
	}

	.user-icon {
		margin-right: 6px;
		color: #363636;
	}

	.user-name {
		font-weight: 500;
		color: #363636;
		margin-right: 8px;
		flex: 1;
		font-size: 0.8rem;
	}

	.user-email {
		color: #666;
		font-size: 0.75rem;
		margin-right: 8px;
		flex: 2;
	}

	.user-badge {
		background-color: #3273dc;
		color: white;
		border-radius: 10px;
		padding: 2px 6px;
		font-size: 0.7rem;
		min-width: 45px;
		text-align: center;
	}

	.admin-badge {
		background-color: #ff3e00;
	}

	.inactive-badge {
		background-color: #999;
	}

	.user-details {
		padding: 10px;
		background-color: rgba(255, 255, 255, 0.98);
		border-top: 1px solid #eee;
		font-size: 0.85rem;
	}

	.user-detail-section {
		margin-bottom: 12px;
	}

	.user-detail-section:last-child {
		margin-bottom: 0;
	}

	.user-detail-header h4 {
		font-size: 0.9rem;
		margin: 0 0 6px 0;
		color: #363636;
		font-weight: 600;
	}

	.detail-item {
		display: flex;
		align-items: center;
		padding: 6px 0;
		border-bottom: 1px solid #f0f0f0;
	}

	.detail-item:last-child {
		border-bottom: none;
	}

	.detail-label {
		flex: 1;
		font-weight: 500;
		color: #363636;
		font-size: 0.8rem;
	}

	.admin-toggle {
		display: flex;
		align-items: center;
	}

	/* Modern toggle switch styles */
	.toggle-field {
		padding: 0.5rem 0;
	}
	
	.toggle-container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		cursor: pointer;
		user-select: none;
	}
	
	.toggle-label {
		font-weight: 500;
		color: #363636;
		flex: 1;
		font-size: 0.75rem;
	}
	
	.toggle-container input[type="checkbox"] {
		opacity: 0;
		width: 0;
		height: 0;
		position: absolute;
	}
	
	.toggle-switch {
		position: relative;
		display: inline-block;
		width: 50px;
		height: 24px;
		background-color: #ccc;
		border-radius: 4px;
		transition: background-color 0.3s;
		flex-shrink: 0;
	}
	
	.toggle-switch::after {
		content: '';
		position: absolute;
		width: 18px;
		height: 18px;
		left: 3px;
		top: 3px;
		background-color: white;
		border-radius: 2px;
		transition: transform 0.3s;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
	}
	
	input[type="checkbox"]:checked + .toggle-switch {
		background-color: #ff3e00;
	}
	
	input[type="checkbox"]:checked + .toggle-switch::after {
		transform: translateX(26px);
	}
	
	input[type="checkbox"]:disabled + .toggle-switch {
		opacity: 0.5;
		cursor: not-allowed;
	}
	
	.toggle-container:hover .toggle-switch:not(:disabled) {
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
	}

	.apps-list {
		display: flex;
		flex-wrap: wrap;
		gap: 4px;
		padding: 6px;
	}

	.app-item {
		display: flex;
		align-items: center;
		background-color: rgba(255, 255, 255, 0.8);
		border: 1px solid #eee;
		border-radius: 4px;
		padding: 2px 6px;
		gap: 4px;
		font-size: 0.75rem;
	}

	.app-name {
		font-size: 0.75rem;
		color: #363636;
	}

	.app-remove {
		background: none;
		border: none;
		color: #ff3860;
		cursor: pointer;
		padding: 1px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		width: 16px;
		height: 16px;
		transition: background-color 0.2s;
	}

	.app-remove:hover {
		background-color: rgba(255, 56, 96, 0.1);
	}

	.add-app {
		background-color: rgba(255, 255, 255, 0.9);
		border: 1px dashed #dbdbdb;
	}

	.app-select {
		border: none;
		background: none;
		font-size: 0.75rem;
		padding: 1px 3px;
		outline: none;
		color: #363636;
	}

	.app-add {
		background: none;
		border: none;
		color: #ff3e00;
		cursor: pointer;
		padding: 1px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		width: 16px;
		height: 16px;
		transition: background-color 0.2s;
	}

	.app-add:hover {
		background-color: rgba(255, 62, 0, 0.1);
	}

	.modal-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
	}

	.modal-content {
		background-color: white;
		border-radius: 8px;
		padding: 2rem;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
		max-width: 400px;
		width: 90%;
		text-align: center;
	}

	.modal-content h3 {
		color: #ff3e00;
		margin-bottom: 1rem;
		font-size: 1.25rem;
	}

	.modal-content p {
		margin-bottom: 1rem;
		color: #363636;
		line-height: 1.4;
	}

	.modal-buttons {
		display: flex;
		gap: 1rem;
		justify-content: center;
		margin-top: 1.5rem;
	}

	.modal-buttons .button.is-danger {
		background-color: #ff3860;
		border-color: #ff3860;
		color: white;
	}

	.modal-buttons .button.is-danger:hover {
		background-color: #ff2348;
		border-color: #ff2348;
	}
</style>

